import logger from '../../../../utils/logging/logger';
import { formatErrorMessage } from './helpers';
import { usePaymentApi, useGuestPayment } from '../paymentApiContext';
import { classifyStripeConfirmError, classifyPaymentIntentFailureMessage } from './stripeSubmitHelpers';

// handleSubmit (the Stripe confirm/submit flow), extracted VERBATIM. Not
// wrapped in useCallback in the original (a plain async function redefined
// every render), so this stays a plain function here too — PaymentSection
// receives it as a normal prop, same as before.
export function useStripeSubmit({
  stripe,
  elements,
  processing,
  success,
  clientSecret,
  clientSecretFromParent,
  setError,
  setPromoError,
  courses,
  initialCoursePricesRef,
  createPaymentIntent,
  setProcessing,
  setClientSecret,
  setPaymentIntentCreated,
  setSuccess,
  productIds,
  navigate,
}) {
  const paymentApi = usePaymentApi(); // signed-in api, or the guest adapter for new students
  const guest = useGuestPayment(); // null for signed-in students
  const handleSubmit = async (e) => {
    e.preventDefault();

    // FIX #9: Prevent duplicate payment submissions
    if (processing || success) {
      logger.debug('Payment submission blocked - already processing or succeeded');
      return;
    }

    if (!stripe || !elements) {
      return;
    }

    // LAZY CREATION: Create payment intent if it doesn't exist
    // This ensures payment intent is only created when user actually clicks "Pay"
    let currentClientSecret = clientSecret || clientSecretFromParent;
    if (!currentClientSecret) {
      logger.info('Creating payment intent on Pay button click (lazy creation)');
      setProcessing(true); // Show loading state
      try {
        // Create payment intent and wait for it to complete
        await createPaymentIntent(true); // Force creation

        // The createPaymentIntent function sets clientSecret via setState (async)
        // We need to wait for React to update the state
        // Use a small delay and then check - the state should be updated by then
        await new Promise(resolve => setTimeout(resolve, 200));

        // Re-check clientSecret after state update
        // Note: We can't directly access updated state in same function, but React will have updated it
        // If still not available, the payment will fail below and user can retry
        currentClientSecret = clientSecret || clientSecretFromParent;

        if (!currentClientSecret) {
          // If still not available, it might be a timing issue
          // Log warning but continue - the payment confirmation will fail gracefully if needed
          logger.warn('Payment intent created but clientSecret not immediately available in state');
          // Don't return here - let the payment flow continue and handle the error below
        } else {
          logger.info('Payment intent created successfully on Pay button click');
        }
      } catch (createError) {
        logger.error('Failed to create payment intent on Pay button click', { error: createError });
        setError(formatErrorMessage('Failed to initialize payment. Please try again.'));
        setProcessing(false);
        return;
      }
    }

    // Final check - if we still don't have clientSecret, show error
    currentClientSecret = clientSecret || clientSecretFromParent;
    if (!currentClientSecret) {
      logger.error('No clientSecret available for payment');
      setError(formatErrorMessage('Payment initialization failed. Please refresh the page and try again.'));
      setProcessing(false);
      return;
    }

    // FIX #15: Check payment intent status before submit
    // FIX #33: Payment intent ownership validation - backend should validate that payment intent
    // belongs to the current user. Frontend cannot fully prevent manipulation, but we validate
    // status to ensure payment intent is in a valid state before confirmation.
    if (currentClientSecret) {
      try {
        const { paymentIntent } = await stripe.retrievePaymentIntent(currentClientSecret);
        if (paymentIntent) {
          // FIX #22: Check if payment intent already succeeded (prevent duplicate confirmation)
          if (paymentIntent.status === 'succeeded') {
            logger.warn('Payment intent already succeeded, redirecting to confirmation');
            // Payment already succeeded, navigate to confirmation
            navigate('/confirmation', {
              state: {
                purchaseData: {
                  success: true,
                  message: 'Payment already completed',
                  paymentIntentId: paymentIntent.id
                }
              }
            });
            return;
          }

          // FIX #16: Handle canceled payment intents
          if (paymentIntent.status === 'canceled') {
            setError(formatErrorMessage('Payment was canceled. Please create a new payment.'));
            setClientSecret(null);
            setPaymentIntentCreated(false);
            return;
          }
        }
      } catch (retrieveError) {
        logger.error('Error checking payment intent status before submit', { error: retrieveError });
        // Continue with submission - might be a transient error
      }
    }

    // FIX #12: Clear all error states at start of submit
    setError(null);
    setPromoError(null);

    // FIX #8: Validate course prices haven't changed since payment intent creation
    const priceMismatches = [];
    courses.forEach(course => {
      const initialPrice = initialCoursePricesRef.current.get(course.id);
      const currentPrice = parseFloat(course.price) || 0;
      if (initialPrice !== undefined && Math.abs(initialPrice - currentPrice) > 0.01) {
        priceMismatches.push({
          courseId: course.id,
          courseName: course.name,
          initialPrice,
          currentPrice
        });
      }
    });

    if (priceMismatches.length > 0) {
      logger.warn('Course price mismatch detected', { priceMismatches });
      const mismatchNames = priceMismatches.map(m => m.courseName).join(', ');
      setError(formatErrorMessage(
        `The price for ${mismatchNames} has changed. Please refresh the page and try again.`
      ));
      // Clear payment intent to force recreation with new prices
      setClientSecret(null);
      setPaymentIntentCreated(false);
      return;
    }

    // FIX #25: Validate payment method before submit
    try {
      // Check if payment method is complete
      const { error: elementsError } = await elements.submit();
      if (elementsError) {
        logger.warn('Payment method validation failed', { error: elementsError });
        setError(formatErrorMessage(elementsError.message || 'Please complete your payment method details.'));
        return;
      }
    } catch (validationError) {
      logger.error('Error validating payment method', { error: validationError });
      // Continue anyway - Stripe will handle validation during confirmation
    }

    setProcessing(true);

    try {
      // Confirm Payment with unified Payment Element
      // Use currentClientSecret which is either clientSecret or clientSecretFromParent
      const currentClientSecretForPayment = clientSecret || clientSecretFromParent;
      const { error: stripeError } = await stripe.confirmPayment({
        elements,
        clientSecret: currentClientSecretForPayment,
        confirmParams: {
          return_url: guest?.returnUrl || (window.location.origin + '/portal/confirmation')
        }
      });

      if (stripeError) {
        // FIX #11: Use formatErrorMessage for consistent error message formatting
        // Enhanced error messages for different failure types
        const errorMessage = classifyStripeConfirmError(stripeError);

        logger.warn('Stripe payment error', {
          errorCode: stripeError.code,
          errorMessage: stripeError.message,
          errorType: stripeError.type
        });

        setError(formatErrorMessage(errorMessage));
        setProcessing(false);
        return; // CRITICAL: Return early - enrollment will NOT be created
      }

      // FIX #5: Removed redundant clientSecret check - if it was falsy, confirmPayment would have already failed
      // The check is unnecessary since we already used clientSecret in confirmPayment above

      // For cards and wallets with no redirect, poll retrievePaymentIntent to check status
      // Note: paymentIntent was already retrieved in the pre-submit check, but retrieve again
      // to get the latest status after confirmPayment
      let paymentIntent;
      try {
        const result = await stripe.retrievePaymentIntent(currentClientSecretForPayment);
        paymentIntent = result.paymentIntent;

        // FIX #20 & #10: Validate payment intent status and handle all statuses
        if (!paymentIntent) {
          setError(formatErrorMessage('Could not retrieve payment intent. Please try again.'));
          setProcessing(false);
          return;
        }
      } catch (retrieveError) {
        logger.error('Error retrieving payment intent after confirmation', { error: retrieveError });
        setError(formatErrorMessage('Could not retrieve payment intent. Please try again.'));
        setProcessing(false);
        return;
      }

      // FIX #10 & #11: Handle all payment intent statuses with consistent error formatting
      // CRITICAL: Enrollment will NOT be created for any status other than 'succeeded'
      switch (paymentIntent.status) {
        case 'requires_payment_method':
          setError(formatErrorMessage('Please complete your payment method details.'));
          setProcessing(false);
          return; // No enrollment

        case 'requires_action':
        case 'requires_confirmation':
          // Payment requires additional action (3D Secure, etc.)
          // Stripe Elements will handle this automatically, but we should inform user
          logger.debug('Payment requires additional action', { status: paymentIntent.status });
          // Continue - Stripe will handle the action and redirect if needed
          break;

        case 'processing':
          setError(formatErrorMessage('Your payment is being processed. Please wait...'));
          setProcessing(false);
          return; // No enrollment - payment not complete

        case 'requires_capture':
          setError(formatErrorMessage('Payment requires capture. Please contact support.'));
          setProcessing(false);
          return; // No enrollment

        case 'canceled':
          // FIX #16: Handle payment intent cancellation
          setError(formatErrorMessage('Payment was canceled. Please try again.'));
          setProcessing(false);
          // Clear client secret to allow new payment intent creation
          setClientSecret(null);
          setPaymentIntentCreated(false);
          return; // No enrollment

        case 'failed': {
          // CRITICAL: Payment failed - show user-friendly error message
          const { userFriendlyMessage, failureCode } = classifyPaymentIntentFailureMessage(paymentIntent);

          logger.warn('Payment failed', {
            paymentIntentId: paymentIntent.id,
            failureCode,
            failureMessage: paymentIntent.last_payment_error?.message
          });

          setError(formatErrorMessage(userFriendlyMessage));
          setProcessing(false);
          // Clear client secret to allow new payment intent creation
          setClientSecret(null);
          setPaymentIntentCreated(false);
          return; // CRITICAL: No enrollment for failed payments
        }

        case 'succeeded':
          // Continue to confirmation - enrollment will be created
          break;

        default:
          logger.warn('Unknown payment intent status', { status: paymentIntent.status });
          setError(formatErrorMessage(`Payment status: ${paymentIntent.status}. Please contact support if this persists.`));
          setProcessing(false);
          return; // No enrollment for unknown status
      }

      if (paymentIntent.status === 'succeeded') {
        // CRITICAL FIX #3: Improved error recovery for backend confirmation failures
        let confirmationResponse;
        try {
          if (productIds) {
            confirmationResponse = await paymentApi.confirmProductPayment({
              paymentIntentId: paymentIntent.id
            });
          } else {
            confirmationResponse = await paymentApi.confirmPayment({
              paymentIntentId: paymentIntent.id
            });
          }

          // Verify confirmation was successful
          if (!confirmationResponse || !confirmationResponse.success) {
            throw new Error(confirmationResponse?.message || 'Backend confirmation failed');
          }

          setSuccess(true);

          // Debug: Log the confirmation response
          logger.debug('Confirmation response', {
            confirmationResponse,
            redirectTo: confirmationResponse.redirectTo,
            productIds
          });

          // Handle different redirect scenarios based on API response
          logger.debug('About to redirect', { redirectTo: confirmationResponse.redirectTo });
          if (confirmationResponse.redirectTo === 'confirmation') {
            logger.debug('Redirecting to confirmation page');
            // Product purchase - show confirmation page
            navigate('/confirmation', {
              state: { purchaseData: confirmationResponse }
            });
          } else {
            logger.debug('Redirecting to confirmation page for course enrollment');
            // Course enrollment - also show confirmation page
            navigate('/confirmation', {
              state: { purchaseData: confirmationResponse }
            });
          }
        } catch (confirmationError) {
          // CRITICAL FIX #3: Handle backend confirmation failure after Stripe success
          logger.error('Backend confirmation failed after Stripe payment succeeded', {
            error: confirmationError,
            paymentIntentId: paymentIntent.id,
            paymentIntentStatus: paymentIntent.status
          });

          // Payment succeeded in Stripe but backend confirmation failed
          // Show error with payment intent ID for support reference
          // FIX #11: Use formatErrorMessage for consistent formatting
          setError(formatErrorMessage(
            `Payment processed successfully, but enrollment confirmation failed. ` +
            `Please contact support with payment ID: ${paymentIntent.id}. ` +
            `Your payment has been received and will be processed manually.`
          ));

          // Don't set success to true since enrollment didn't complete
          // User will need to contact support
        }
      } else if (paymentIntent && paymentIntent.status !== 'succeeded') {
        // Payment intent exists but hasn't succeeded yet
        logger.warn('Payment intent not succeeded', {
          status: paymentIntent.status,
          paymentIntentId: paymentIntent.id
        });
        // FIX #11: Use formatErrorMessage for consistent formatting
        setError(formatErrorMessage(`Payment status: ${paymentIntent.status}. Please try again or contact support.`));
      }
    } catch (error) {
      logger.error('Payment error', { error });
      // FIX #11: Use formatErrorMessage for consistent error message formatting
      // FIX #20: Distinguish between network errors, timeout errors, and server errors
      const isTimeoutError = error.isTimeout || error.message?.includes('timeout') || error.name === 'AbortError';
      const isNetworkError = !error.response && (error.message?.includes('fetch') || error.message?.includes('network'));
      const errorMessage = isTimeoutError
        ? 'Request timed out. Please check your internet connection and try again.'
        : isNetworkError
        ? 'Network error. Please check your connection and try again.'
        : (error.response?.data?.message || error.message || 'Payment failed. Please try again.');
      setError(formatErrorMessage(errorMessage));
    } finally {
      setProcessing(false);
    }
  };

  return { handleSubmit };
}
